import * as Sentry from "@sentry/react"
import { auth } from "auth"
import type { FetchError } from "fetcher"
import { DialogContainer } from "materia"
import ReactDOM from "react-dom/client"
import { ConnectionIssueModal } from "../components/ConnectionIssueModal"
import {
  API_TOKEN,
  APP_ENV,
  AUTH_URL,
  BACKOFFICE_API_URL,
  LOGIN_URL,
} from "../env"

type Config = {
  storybookMode?: boolean
}
export const initializeAuth = async ({ storybookMode }: Config = {}) => {
  try {
    auth.initializeAuth({
      authUrl: AUTH_URL,
      loginUrl: LOGIN_URL,
      backofficeUrl: BACKOFFICE_API_URL,
      apiToken: API_TOKEN,
      subdomain: storybookMode ? undefined : auth.getSubdomainFromPath(),
      redirectStrategy: storybookMode ? "href" : undefined,
      environment: storybookMode ? "test" : APP_ENV,
      locale: undefined,
    })

    await revalidateExistingSession()
  } catch (error) {
    Sentry.captureException(error)
    // We don't really know what kinda error we have here,
    // so we show connection issue for everything for now.
    // It should be accurate for about 90% of issues anyway
    showConnectionIssueModal()
  }
}

/**
 * If there's an existing access token in local storage, it might've expired.
 * Without a check on mount, the app will throw a bunch of unhandled 401s
 * before redirecting to login. Revalidating the session on mount prevents it
 */
const revalidateExistingSession = async () => {
  try {
    const { accessToken } = await auth.getActiveSession()

    const isAccessTokenNonEmptyString =
      typeof accessToken === "string" && accessToken.length > 0

    if (!isAccessTokenNonEmptyString) {
      await auth.refreshLogin()
      return
    }

    await auth.accessTokenToTransferKey(accessToken)
  } catch (error: unknown) {
    // TODO: Figure out if we should treat any error with a re-direct to login
    if ((error as FetchError).status === 401) {
      await auth.refreshLogin()
    } else {
      throw error
    }
  }
}
// If user really is offline, they won't be able to go through
// the auth flow before React root is even rendered. So we have to
// create a root here only to show the offline warning
//
// TODO: We should do all initialization after the React root is rendered.
// This allows us to show a skeleton instead of blank screen while initializing
// and then we wouldn't need the method below either
let offlineRoot: ReactDOM.Root | null = null

const showConnectionIssueModal = () => {
  const rootElement = document.getElementById("root")!

  if (!offlineRoot) {
    offlineRoot = ReactDOM.createRoot(rootElement)
  }

  offlineRoot.render(
    <DialogContainer>
      <ConnectionIssueModal />
    </DialogContainer>
  )
}
